<template>
  <div class="green-fitout-folders">
    <PostFitOutGuideline
      :guidelines="currentFolders"
      :enableExpandByClickingTitle="enableExpandByClickingTitle"
      :expand="true"
      ref="guidelineRef"
    >
      <template
        #secTitle="secTitleProps"
      >
        <HKLLeftRight class="green-fitout-folders__title-box">
          <div class="green-fitout-folders__title-left">
            <span>{{ secTitleProps.section.displayOrder }}. {{ secTitleProps.section.value }}</span>
          </div>
          <HKLLeftRight class="green-fitout-folders__title-right">
            <template #left>
              <div
                v-if="canViewPoint && hasAccepted(secTitleProps.section.sectionContent)"
                class="green-fitout-folders__point font-bold"
                :class="{completed: isComplete(secTitleProps.section)}"
              >
                {{ totalPoints(secTitleProps.section.sectionContent) }} {{$t('tenantProjects.greenFitOut.pts')}}
              </div>
            </template>
            <template #right>
              <HKLStatusBadge
                v-if="isComplete(secTitleProps.section) && !isTenant"
                color="light-green"
                :value="$t('status.complied') | uppercase"
              />
              <HKLButton
                v-if="canReviewGreenFitout && !isComplete(secTitleProps.section)"
                color="orange"
                action="comply"
                :disabled="!canComplete(secTitleProps.section)"
                @click="() => complyPopupAction(secTitleProps.index)"
              />
              <HKLModal
                :ref="`complyModal-${secTitleProps.index}`"
                okBtnAction="yesComply"
                :title="$t('tenantProjects.greenFitOut.complyAlert')"
                :afterClose="resetClicking"
                @onOkClick="() => complyAction(secTitleProps.index)"
              />
            </template>
          </HKLLeftRight>
        </HKLLeftRight>
      </template>
      <template #item="sectionItemProps">
        <GreenFitOutSubFolders
          :folder="sectionItemProps.section"
          :subFolder="sectionItemProps.sectionItem"
        />
      </template>
    </PostFitOutGuideline>
  </div>
</template>

<script>
import lodash from 'lodash'
import { getCurrentInstance, ref, reactive, toRefs, unref, computed } from 'vue-demi'
import np from 'number-precision'
import { useStoreGetters } from '@/store'
import { updateFoldersForGreenFitoutSubmission } from '@/api/tenantProjects/greenFitout.request'
import HKLButton from '@/controls/Button/HKLButton'
import HKLModal from '@/controls/Modal/HKLModal'
import HKLStatusBadge from '@/controls/Badge/HKLStatusBadge'
import HKLLeftRight from '@/controls/Layout/HKLLeftRight'
import PostFitOutGuideline from '../postFitOutLetter/PostFitOutGuideline'
import GreenFitOutSubFolders from './GreenFitOutSubFolders'
import { GREEN_FITOUT_FOLDER_STATUS } from './consts'
import { useRefreshCurrentGreenFitout, useCurrentGreenFitout, useGreenFitoutPerms, useFoldersCollapseToggleEvent } from './hook'

export default {
  components: {
    HKLButton,
    GreenFitOutSubFolders,
    PostFitOutGuideline,
    HKLModal,
    HKLStatusBadge,
    HKLLeftRight,
  },
  setup () {
    const { proxy } = getCurrentInstance()
    const userStore = useStoreGetters('user', ['isTenant', 'isEngineer'])
    const submission = useCurrentGreenFitout()
    const { canViewPoint, canReviewGreenFitout } = useGreenFitoutPerms()
    const refresh = useRefreshCurrentGreenFitout()
    const guidelineRef = ref(null)
    const collapseRefs = computed(() => {
      const ref = unref(guidelineRef)
      return ref ? ref.collapseRefs : undefined
    })
    useFoldersCollapseToggleEvent(collapseRefs)
    const setupData = reactive({
      ...userStore,
      guidelineRef,
      enableExpandByClickingTitle: true,
      canViewPoint,
      canReviewGreenFitout,
      currentFolders: computed(() => unref(submission).folders),
      totalPoints: (items = []) => {
        return np.plus(...items.map((item) => Number(item.point || 0)))
      },
      hasAccepted: (items = []) => {
        return items.some(item => item.status === GREEN_FITOUT_FOLDER_STATUS.ACCEPTED)
      },
      isComplete: (item) => item.status === GREEN_FITOUT_FOLDER_STATUS.COMPLETED,
      canComplete: (item) => {
        const hasNotAcceptedItem = item.sectionContent.some(item => item.status !== GREEN_FITOUT_FOLDER_STATUS.ACCEPTED)
        return !hasNotAcceptedItem && setupData.totalPoints(item.sectionContent) >= 0
      },
      complyPopupAction: (i) => {
        setupData.enableExpandByClickingTitle = false
        proxy.$refs[`complyModal-${i}`].open()
      },
      complyAction: async (i) => {
        const metadata = { status: GREEN_FITOUT_FOLDER_STATUS.COMPLETED }
        Object.assign(setupData.currentFolders[i], metadata)
        await updateFoldersForGreenFitoutSubmission(unref(submission).id, {
          folders: [lodash.pick(setupData.currentFolders[i], ['displayOrder', 'value', 'status'])],
        })
        await refresh()
      },
      updateSubFolder: async (i, j, obj) => {
        Object.assign(
          setupData.currentFolders[i].sectionContent[j],
          lodash.omit(obj, ['id', 'displayOrder', 'order', 'value']),
        )
        await updateFoldersForGreenFitoutSubmission(unref(submission).id, { folders: setupData.currentFolders })
        await refresh()
      },
      resetClicking () {
        setupData.enableExpandByClickingTitle = true
      },
    })

    return {
      ...toRefs(setupData),
    }
  },
}
</script>

<style lang="scss" scoped>
@import './folders.scss';
.green-fitout-folders {
  &__title-right {
    font-size: 14px;
  }
  ::v-deep {
    .collapse.expanded .title-container {
      margin-bottom: 0;
    }
    .collapse .content.with-border {
      margin-bottom: 0;
    }
    .hkl-guideline__child-contentbox {
      padding: 0;
      border: none;
    }
    .green-fitout-folders__point.font-bold {
      font-weight: bold;
    }
  }
}
</style>
